import { useCallback, useMemo } from 'react';
import { microServicesRoutes } from '@constants';
import { contactsApi, edgeApi, syncApi } from '@lib/apiClients';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';

import type {
  ApiResponse,
  ContactInfo,
  DashboardFilters,
  DashboardSession,
  SessionMetricsSummary,
  IntegrationJob,
  Job,
  OrganizationInfo,
  PaginatedResponse, PaginatedWithMetricsResponse,
  SdrInfo,
  SessionDetails,
  SessionRecordings,
  SessionCallsFilters,
  SessionContactSummary,
  ContactTimeline,
} from '../types/dashboard.types';
import type { OverviewQueryParams } from '@types';
import { logWarning } from '@lib/logger/logWarning.ts';
import { useTranslation } from 'react-i18next';

export type ReportType = 'raw' | 'compact';

// Module-scoped dedupe for getOrganizationsAsync: multiple callers (filter bar,
// session enrichment) on the same page would otherwise each issue a GET /organization.
// The list rarely changes, so cache the resolved response for 5 minutes.
// Pickers over org/SDR lists ask for one bounded page and search on the
// server. The lists were unbounded: every dashboard load pulled all 155 active
// SDRs in QA, with nothing capping growth, and QA holds roughly a fifteenth of
// production's data.
const PICKER_PAGE_SIZE = 50;

const ORG_CACHE_TTL_MS = 5 * 60 * 1000;
let orgCache: { fetchedAt: number; data: OrganizationInfo[] } | null = null;
let pendingOrgsPromise: Promise<ApiResponse<OrganizationInfo[]>> | null = null;

export interface UseDashboardApiReturn {
  getSessionsAsync: (
    userId: string,
    filters: DashboardFilters,
    page: number,
    pageLimit: number,
    sdrIds?: string[],
    orgIds?: string[],
  ) => Promise<ApiResponse<PaginatedResponse<DashboardSession>>>;
  getSessionDetailsAsync: (
    callSessionId: string,
    userId: string,
    page: number,
    limit: number,
    filters?: SessionCallsFilters,
  ) => Promise<ApiResponse<PaginatedResponse<SessionDetails>>>;
  getRecordingsAsync: (
    callId: string,
    userId: string,
    contactId?: string,
  ) => Promise<ApiResponse<SessionRecordings>>;
  getSdrsAsync: (
    userId: string,
    userIds: string[] | null,
    organizationId: string | null,
    search?: string,
  ) => Promise<ApiResponse<SdrInfo[]>>;
  getOrganizationsAsync: (search?: string) => Promise<ApiResponse<OrganizationInfo[]>>;
  getSessionContactSummaryAsync: (
    callSessionId: string,
  ) => Promise<ApiResponse<SessionContactSummary>>;
  getContactByIdAsync: (contactId: string) => Promise<ApiResponse<ContactInfo>>;
  getContactTimelineAsync: (
    callSessionId: string,
    contactId: string,
    callId: string,
  ) => Promise<ApiResponse<ContactTimeline>>;
  downloadReportAsync: (
    callSessionId: string,
    reportType: ReportType,
    organizationId?: string,
  ) => Promise<ApiResponse<undefined>>;
  getSessionMetricsSummaryAsync: (callSessionId: string) => Promise<ApiResponse<SessionMetricsSummary>>;
  getOverviewKpisAsync: (params: OverviewQueryParams) => Promise<ApiResponse<unknown>>;
  getOverviewLeaderboardAsync: (params: OverviewQueryParams) => Promise<ApiResponse<unknown>>;
  getHubspotJobs: (
      organizationId: string,
      filters: DashboardFilters,
      page: string,
      pageLimit: string,
  ) => Promise<ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>>>;
  getOutreachJobs: (
      organizationId: string,
      filters: DashboardFilters,
      page: string,
      pageLimit: string,
  ) => Promise<ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>>>;
  getSalesforceJobs: (
      organizationId: string,
      filters: DashboardFilters,
      page: string,
      pageLimit: string,
  ) => Promise<ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>>>;
  retryHubspotJob: (
      jobId: string,
  ) => Promise<ApiResponse<Job>>;
  retryOutreachJob: (
      jobId: string,
  ) => Promise<ApiResponse<Job>>;
  retrySalesforceJob: (
      jobId: string,
  ) => Promise<ApiResponse<Job>>;
}

export default function useDashboardApi (): UseDashboardApiReturn {
  const { t } = useTranslation();

  const handleError = useCallback(<T = never>(
    message: string,
    error: unknown,
    source: string,
    context?: Record<string, unknown>,
  ): ApiResponse<T> => {
    const errorMessage = extractErrorMessage(error);

    if (extractErrorMessage(error).includes('404')) {
      void logWarning(t('dashboard.useDashboardApi.withoutDetails', 'Without session details.'), source, {
        ...context,
        error: errorMessage,
        rawError: error,
      }, false);
    } else {
      void logError(message, source, {
        ...context,
        error: errorMessage,
        rawError: error,
      }, false);
    }

    return { success: false, error: { message, raw: errorMessage } };
  }, []);

  const getSessionsAsync = useCallback(async (
    userId: string,
    filters: DashboardFilters,
    page: number,
    pageLimit: number,
    sdrIds?: string[],
    orgIds?: string[],
  ): Promise<ApiResponse<PaginatedResponse<DashboardSession>>> => {
    const source = 'Dashboard.getSessionsAsync';
    try {
      const params: Record<string, string | number> = {
        page,
        pageLimit,
      };

      if (filters.searchQuery !== '') {
        params.searchQuery = filters.searchQuery;
      }

      // Filtering by status means "active", and active ignores the calendar:
      // a session paused three weeks ago is still paused. Dropping the dates
      // HERE rather than blanking them in the store keeps the user's range
      // intact — the overview reads the same filters and must not be affected.
      const ignoreDateRange = filters.statuses.length > 0;

      // Add date filters (new v3 params)
      // Handle both Date objects and ISO strings (from persisted store)
      if (!ignoreDateRange && filters.dateRange.from !== undefined && filters.dateRange.from !== null) {
        const fromDate = filters.dateRange.from instanceof Date
          ? filters.dateRange.from
          : new Date(filters.dateRange.from);
        params.dateFrom = fromDate.toISOString();
      }
      if (!ignoreDateRange && filters.dateRange.to !== undefined && filters.dateRange.to !== null) {
        const toDate = filters.dateRange.to instanceof Date
          ? filters.dateRange.to
          : new Date(filters.dateRange.to);
        params.dateTo = toDate.toISOString();
      }

      // Sent as a comma-separated list because "active" is two statuses.
      // Absent when empty, so the default request is byte-for-byte what it was.
      if (filters.statuses.length > 0) {
        params.status = filters.statuses.join(',');
      }

      const requestUrl = `${microServicesRoutes.contacts.get.dashboard.getSessions}/${userId}`;

      const response = await contactsApi.post<PaginatedResponse<DashboardSession>>(
        requestUrl,
        {
          sdrIds: sdrIds ?? filters.sdrIds,
          orgIds: orgIds ?? filters.orgIds ?? [],
          listTypes: filters.listTypes.length > 0 ? filters.listTypes : undefined,
        },
        { params },
      );

      void logInfo('Dashboard sessions fetched.', source, {
        userId,
        page,
        totalCount: response.data?.totalCount,
        dateFrom: filters.dateRange.from,
        dateTo: filters.dateRange.to,
      });

      return {
        success: true,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedResponse<DashboardSession>>(
        'Error fetching dashboard sessions.',
        error,
        source,
        { userId, page, pageLimit },
      );
    }
  }, [handleError]);

  const getSessionDetailsAsync = useCallback(async (
    callSessionId: string,
    userId: string,
    page: number,
    limit: number,
    filters: SessionCallsFilters = {},
  ): Promise<ApiResponse<PaginatedResponse<SessionDetails>>> => {
    const source = 'Dashboard.getSessionDetailsAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.dashboard.getSessionDetails}/${callSessionId}/user/${userId}`;

      // Filters go to the server: it returns the matching page, rather than the
      // frontend downloading the whole session and filtering it in memory.
      const response = await contactsApi.get<PaginatedResponse<SessionDetails>>(
        requestUrl,
        {
          params: {
            page,
            limit,
            ...(filters.search !== undefined && filters.search !== '' ? { search: filters.search } : {}),
            ...(filters.disposition !== undefined && filters.disposition !== 'all' ? { disposition: filters.disposition } : {}),
            ...(filters.action !== undefined && filters.action !== 'all' ? { action: filters.action } : {}),
            ...(filters.agentId !== undefined && filters.agentId !== 'all' ? { agentId: filters.agentId } : {}),
            ...(filters.tab !== undefined && filters.tab !== 'all' ? { tab: filters.tab } : {}),
          },
        },
      );

      void logInfo('Session details fetched.', source, {
        callSessionId,
        userId,
        page,
      });

      return {
        success: true,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedResponse<SessionDetails>>(
        'Error fetching session details.',
        error,
        source,
        { callSessionId, userId },
      );
    }
  }, [handleError]);

  const getRecordingsAsync = useCallback(async (
    callId: string,
    userId: string,
    contactId?: string,
  ): Promise<ApiResponse<SessionRecordings>> => {
    const source = 'Dashboard.getRecordingsAsync';
    try {
      const baseUrl = `${microServicesRoutes.contacts.get.dashboard.getRecordings}/${callId}/user/${userId}`;
      // Pass contactId so the backend can reject cross-contact recording
      // mismatches (migrated Twilio data can point a conference's legs at
      // another contact's recording).
      const requestUrl = contactId != null && contactId !== ''
        ? `${baseUrl}?contactId=${contactId}`
        : baseUrl;

      const response = await contactsApi.get<SessionRecordings>(requestUrl);

      void logInfo('Recordings fetched.', source, { callId, userId, contactId });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<SessionRecordings>(
        'Error fetching recordings.',
        error,
        source,
        { callId, userId, contactId },
      );
    }
  }, [handleError]);

  const getSdrsAsync = useCallback(async (
    userId: string,
    userIds: string[] | null,
    organizationId: string | null,
    search?: string,
  ): Promise<ApiResponse<SdrInfo[]>> => {
    const source = 'Dashboard.getSdrsAsync';
    try {
      // `userIds` resolves specific people (turning ids into names) and is
      // already bounded by the caller — the backend ignores search/paging in
      // that mode. A typed search asks for one bounded page matching it. The
      // unsearched call sends no paging on purpose: it is the list the filter
      // dropdown and the SDR-name map read, and the backend only caches the
      // unfiltered list — sending paging would both cap it at 50 and bypass
      // that cache on every dashboard load.
      const isIdLookup = userIds !== null && userIds.length > 0;
      const isSearching = search !== undefined && search.trim() !== '';

      const { data } = await edgeApi.get<SdrInfo[]>(
        `${microServicesRoutes.edge.get.users.allActiveSdrs}/${userId}`,
        {
          params: {
            userIds,
            organizationId,
            ...(!isIdLookup && isSearching
              ? { search, page: 1, pageLimit: PICKER_PAGE_SIZE }
              : {}),
          },
        },
      );

      void logInfo('SDRs fetched.', source, { userId, count: data?.length });

      return { success: true, data: data ?? [] };
    } catch (error) {
      return handleError<SdrInfo[]>(
        'Error fetching SDRs.',
        error,
        source,
        { userId },
      );
    }
  }, [handleError]);

  const fetchOrganizations = useCallback(async (
    search?: string,
  ): Promise<ApiResponse<OrganizationInfo[]>> => {
    const source = 'Dashboard.getOrganizationsAsync';
    try {
      const url = microServicesRoutes.edge.get.organizations.getAllActiveOrganizations;

      type ServerDto =
        | OrganizationInfo[]
        | { data?: OrganizationInfo[]; activeOrganizations?: OrganizationInfo[] };

      // A typed search gets one bounded page, filtered server-side. The
      // unsearched call stays unbounded on purpose: it is what the filter
      // dropdown, the org-name map and the default "Tendril" org selection
      // read, and all three need every organization, not the first 50.
      // Organizations are few; SDRs are the list that needed the cap.
      const isSearching = search !== undefined && search !== '';
      const response = await edgeApi.get<ServerDto>(url, {
        params: isSearching
          ? { search, page: 1, pageLimit: PICKER_PAGE_SIZE }
          : {},
      });

      const list: OrganizationInfo[] = Array.isArray(response.data)
        ? response.data
        : response.data?.data ?? response.data?.activeOrganizations ?? [];

      void logInfo('Organizations fetched.', source, { count: list.length, search });

      return { success: true, data: list };
    } catch (error) {
      return handleError<OrganizationInfo[]>(
        'Error fetching organizations.',
        error,
        source,
      );
    }
  }, [handleError]);

  const getOrganizationsAsync = useCallback(async (
    search?: string,
  ): Promise<ApiResponse<OrganizationInfo[]>> => {
    const isSearching = search !== undefined && search !== '';

    // The cache and the in-flight de-duplication only apply to the default,
    // unsearched page. A searched query is specific to what the user typed and
    // must not be served from — or written to — that cache.
    if (!isSearching && orgCache !== null && Date.now() - orgCache.fetchedAt < ORG_CACHE_TTL_MS) {
      return { success: true, data: orgCache.data };
    }

    if (isSearching) {
      return await fetchOrganizations(search);
    }

    // De-duplicate concurrent callers (page mount + session enrichment).
    if (pendingOrgsPromise !== null) {
      return await pendingOrgsPromise;
    }

    pendingOrgsPromise = fetchOrganizations(undefined).then((result) => {
      if (result.success) {
        orgCache = { fetchedAt: Date.now(), data: result.data };
      }
      return result;
    });

    try {
      return await pendingOrgsPromise;
    } finally {
      pendingOrgsPromise = null;
    }
  }, [handleError]);

  /**
   * Contacts and fuego leads for a session, counted by the backend and scoped
   * to the contacts the session actually called.
   *
   * The session detail used to download every contact of the LIST to compute
   * these two numbers in the browser — which answered a different question
   * (the size of the whole list, and every flagged contact in it, whether or
   * not the session dialled them) and ran the contacts controller's
   * duplicate-detection scan over the list on every session view.
   */
  const getSessionContactSummaryAsync = useCallback(async (
    callSessionId: string,
  ): Promise<ApiResponse<SessionContactSummary>> => {
    const source = 'Dashboard.getSessionContactSummaryAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.dashboard.sessionContactSummary}/${callSessionId}/summary`;
      const response = await contactsApi.get<SessionContactSummary>(requestUrl);

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<SessionContactSummary>(
        'Error fetching session contact summary.',
        error,
        source,
        { callSessionId },
      );
    }
  }, [handleError]);

  /**
   * The path one call took, fetched when its row is expanded.
   *
   * Deliberately not part of the calls list: a page of 20 rows carries roughly
   * 80 events and almost nobody opens them.
   */
  const getContactTimelineAsync = useCallback(async (
    callSessionId: string,
    contactId: string,
    callId: string,
  ): Promise<ApiResponse<ContactTimeline>> => {
    const source = 'Dashboard.getContactTimelineAsync';
    try {
      // `callId` scopes the path to THIS row's call. A contact dialled twice in
      // one session otherwise gets both calls' events in every one of its rows.
      const requestUrl = `${microServicesRoutes.contacts.get.dashboard.sessionContactSummary}/${callSessionId}/contact/${contactId}/timeline?callId=${encodeURIComponent(callId)}`;
      const response = await contactsApi.get<ContactTimeline>(requestUrl);

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<ContactTimeline>(
        'Error fetching the call path.',
        error,
        source,
        { callSessionId, contactId, callId },
      );
    }
  }, [handleError]);

  const getContactByIdAsync = useCallback(async (
    contactId: string,
  ): Promise<ApiResponse<ContactInfo>> => {
    const source = 'Dashboard.getContactByIdAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.contact.getById}/${contactId}`;

      const response = await contactsApi.get<ContactInfo>(requestUrl);

      void logInfo('Contact info fetched.', source, { contactId });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<ContactInfo>(
        'Error fetching contact info.',
        error,
        source,
        { contactId },
      );
    }
  }, [handleError]);

  const downloadReportAsync = useCallback(async (
    callSessionId: string,
    reportType: 'raw' | 'compact',
    organizationId?: string,
  ): Promise<ApiResponse<undefined>> => {
    const source = 'Dashboard.downloadReportAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.callSession.getReport}/${callSessionId}`;

      const params: Record<string, string> = { type: reportType };
      if (reportType === 'raw' && organizationId !== undefined && organizationId !== '') {
        params.organizationId = organizationId;
      }

      const response = await contactsApi.get<Blob>(requestUrl, {
        params,
        responseType: 'blob',
      });

      // Prefer the filename the backend sets via Content-Disposition; fall back
      // to a local name. The backend now sends the proper text/csv content-type,
      // so we use the response blob as-is instead of forcing the MIME here.
      let filename = `report-${callSessionId}-${reportType}.csv`;
      const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
      if (typeof contentDisposition === 'string') {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1] != null && match[1] !== '') {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      void logInfo('Report downloaded.', source, { callSessionId, reportType });

      return { success: true, data: undefined };
    } catch (error) {
      return handleError<undefined>(
        'Error downloading report.',
        error,
        source,
        { callSessionId, reportType },
      );
    }
  }, [handleError]);



  const getSessionMetricsSummaryAsync = useCallback(async (
    callSessionId: string,
  ): Promise<ApiResponse<SessionMetricsSummary>> => {
    const source = 'Dashboard.getSessionMetricsSummaryAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.get.metrics.sessionSummary}/${callSessionId}/summary`;
      const response = await contactsApi.get<SessionMetricsSummary>(requestUrl);

      return { success: true, data: response.data };
    } catch (error) {
      return handleError<SessionMetricsSummary>(
        'Error fetching session metrics summary.',
        error,
        source,
        { callSessionId },
      );
    }
  }, [handleError]);

  const getOverviewKpisAsync = useCallback(async (
    params: OverviewQueryParams,
  ): Promise<ApiResponse<unknown>> => {
    const source = 'Dashboard.getOverviewKpisAsync';
    try {
      const response = await contactsApi.get(
        microServicesRoutes.contacts.get.dashboard.overviewKpis,
        { params },
      );

      void logInfo('Overview KPIs fetched.', source, {
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
      });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError(
        'Error fetching overview KPIs.',
        error,
        source,
        { dateFrom: params.dateFrom, dateTo: params.dateTo },
      );
    }
  }, [handleError]);

  const getOverviewLeaderboardAsync = useCallback(async (
    params: OverviewQueryParams,
  ): Promise<ApiResponse<unknown>> => {
    const source = 'Dashboard.getOverviewLeaderboardAsync';
    try {
      const response = await contactsApi.get(
        microServicesRoutes.contacts.get.dashboard.overviewLeaderboard,
        { params },
      );

      void logInfo('Overview leaderboard fetched.', source, {
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
      });

      return { success: true, data: response.data };
    } catch (error) {
      return handleError(
        'Error fetching overview leaderboard.',
        error,
        source,
        { dateFrom: params.dateFrom, dateTo: params.dateTo },
      );
    }
  }, [handleError]);

  const getHubspotJobs = useCallback(async (
    organizationId: string,
    filters: DashboardFilters,
    page: string,
    pageLimit: string,
  ) => {
    const fromDate = filters.dateRange.from;
    const toDate = filters.dateRange.to;

    try {
      const params: Record<string, string | Date> = {
        page,
        pageLimit
      };

      if (fromDate !== undefined && fromDate !== null) {
        params.startRangeDate = fromDate instanceof Date
          ? fromDate
          : new Date(fromDate);
      }
      if (toDate !== undefined && toDate !== null) {
        params.endRangeDate = toDate instanceof Date
          ? toDate
          : new Date(toDate);
      }

      const requestUrl = `${microServicesRoutes.sync.get.hubspot.dashboard}/${organizationId}`;

      const response = await syncApi.get<PaginatedWithMetricsResponse<IntegrationJob>>(requestUrl,
        { params }
      );

      void logInfo('Hubspot jobs fetched.', 'Dashboard.getHubspotJobs', { organizationId, toDate, fromDate, page }, false);

      return {
        success: true as const,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedWithMetricsResponse<IntegrationJob>>('Error fetching Hubspot jobs.', error, 'Dashboard.getHubspotJobs', { organizationId, toDate, fromDate, page });
    }
  }, [handleError]);

  const getOutreachJobs = useCallback(async (
    organizationId: string,
    filters: DashboardFilters,
    page: string,
    pageLimit: string,
  ) => {
    const fromDate = filters.dateRange.from;
    const toDate = filters.dateRange.to;

    try {
      const params: Record<string, string | Date> = {
        page,
        pageLimit
      };

      if (fromDate !== undefined && fromDate !== null) {
        params.startRangeDate = fromDate instanceof Date
          ? fromDate
          : new Date(fromDate);
      }
      if (toDate !== undefined && toDate !== null) {
        params.endRangeDate = toDate instanceof Date
          ? toDate
          : new Date(toDate);
      }

      const requestUrl = `${microServicesRoutes.sync.get.outreach.dashboard}/${organizationId}`;

      const response = await syncApi.get<PaginatedWithMetricsResponse<IntegrationJob>>(requestUrl,
        { params }
      );

      void logInfo('Outreach jobs fetched.', 'Dashboard.getOutreachJobs', { organizationId, toDate, fromDate, page }, false);

      return {
        success: true as const,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedWithMetricsResponse<IntegrationJob>>('Error fetching Outreach jobs.', error, 'Dashboard.getOutreachJobs', { organizationId, toDate, fromDate, page });
    }
  }, [handleError]);

  const getSalesforceJobs = useCallback(async (
    organizationId: string,
    filters: DashboardFilters,
    page: string,
    pageLimit: string,
  ) => {
    const fromDate = filters.dateRange.from;
    const toDate = filters.dateRange.to;

    try {
      const params: Record<string, string | Date> = {
        page,
        pageLimit
      };

      if (fromDate !== undefined && fromDate !== null) {
        params.startRangeDate = fromDate instanceof Date
          ? fromDate
          : new Date(fromDate);
      }
      if (toDate !== undefined && toDate !== null) {
        params.endRangeDate = toDate instanceof Date
          ? toDate
          : new Date(toDate);
      }

      const requestUrl = `${microServicesRoutes.sync.get.salesforce.dashboard}/${organizationId}`;

      const response = await syncApi.get<PaginatedWithMetricsResponse<IntegrationJob>>(requestUrl,
        { params }
      );

      void logInfo('Salesforce jobs fetched', 'Dashboard.getSalesforceJobs', { organizationId, toDate, fromDate, page }, false);

      return {
        success: true as const,
        data: response.data,
        totalPages: response.data?.totalPages,
        totalCount: response.data?.totalCount,
        pageNumber: response.data?.pageNumber,
      };
    } catch (error) {
      return handleError<PaginatedWithMetricsResponse<IntegrationJob>>('Error fetching Salesforce jobs', error, 'Dashboard.getSalesforceJobs', { organizationId, toDate, fromDate, page });
    }
  }, [handleError]);

  const retryHubspotJob = useCallback( async (
    jobId: string,
  ): Promise<ApiResponse<Job>> => {
    try {
      const requestUrl = `${microServicesRoutes.sync.post.hubspot.retryJob}/${jobId}`;
      const response = await syncApi.post<Job>(requestUrl);

      void logInfo('Hubspot job retried', 'Dashboard.retryHubspotJob', { jobId }, false);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return handleError<Job>('Error retrying Hubspot job', error, 'Dashboard.retryHubspotJob', { jobId });
    }
  }, [handleError]);

  const retryOutreachJob = useCallback( async (
    jobId: string,
  ): Promise<ApiResponse<Job>> => {
    try {
      const requestUrl = `${microServicesRoutes.sync.post.outreach.retryJob}/${jobId}`;
      const response = await syncApi.post<Job>(requestUrl);

      void logInfo('Outreach job retried', 'Dashboard.retryOutreachJob', { jobId }, false);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return handleError<Job>('Error retrying Outreach job', error, 'Dashboard.retryOutreachJob', { jobId });
    }
  }, [handleError]);

  const retrySalesforceJob = useCallback( async (
    jobId: string,
  ): Promise<ApiResponse<Job>> => {
    try {
      const requestUrl = `${microServicesRoutes.sync.post.salesforce.retryJob}/${jobId}`;
      const response = await syncApi.post<Job>(requestUrl);

      void logInfo('Salesforce job retried', 'Dashboard.retrySalesforceJob', { jobId }, false);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return handleError<Job>('Error retrying Salesforce job', error, 'Dashboard.retrySalesforceJob', { jobId });
    }
  }, [handleError]);

  return useMemo(() => ({
    getSessionsAsync,
    getSessionDetailsAsync,
    getRecordingsAsync,
    getSdrsAsync,
    getOrganizationsAsync,
    getSessionContactSummaryAsync,
    getContactByIdAsync,
    getContactTimelineAsync,
    downloadReportAsync,
    getSessionMetricsSummaryAsync,
    getOverviewKpisAsync,
    getOverviewLeaderboardAsync,
    getHubspotJobs,
    getOutreachJobs,
    getSalesforceJobs,
    retryHubspotJob,
    retryOutreachJob,
    retrySalesforceJob
  }), [
    getSessionsAsync,
    getSessionDetailsAsync,
    getRecordingsAsync,
    getSdrsAsync,
    getOrganizationsAsync,
    getSessionContactSummaryAsync,
    getContactByIdAsync,
    getContactTimelineAsync,
    downloadReportAsync,
    getSessionMetricsSummaryAsync,
    getOverviewKpisAsync,
    getOverviewLeaderboardAsync,
    getHubspotJobs,
    getOutreachJobs,
    getSalesforceJobs,
    retryHubspotJob,
    retryOutreachJob,
    retrySalesforceJob
  ]);
}